"use client";

import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { useLocale } from "next-intl";
import { Check } from "lucide-react";
import { cn } from "@/lib/cn";

/** „Nummer kopiert“ je Sprache. Bewusst hier statt in `messages/*.json`:
 *  die werden beim prebuild aus Sanity (uiStrings) neu erzeugt. DE als
 *  Rückfall. */
const COPIED: Record<string, string> = {
  de: "Nummer kopiert",
  en: "Number copied",
  fr: "Numéro copié",
  nl: "Nummer gekopieerd",
  it: "Numero copiato",
  pl: "Numer skopiowany",
  es: "Número copiado",
  cs: "Číslo zkopírováno",
  zh: "号码已复制",
  da: "Nummer kopieret",
  sv: "Numret kopierat",
  hu: "Szám kimásolva",
  el: "Ο αριθμός αντιγράφηκε",
  tr: "Numara kopyalandı",
  fi: "Numero kopioitu",
  no: "Nummer kopiert",
  pt: "Número copiado",
};

/** Maus/Trackpad als Hauptzeiger: dort führt `tel:` oft ins Leere. */
export function isFinePointer(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: fine)").matches
  );
}

/**
 * Telefon-Link (Buchungs-App 01.10.2026, auf www seit 07.10.2026 überall, wo
 * die Hotline am Desktop klickbar ist): Mit Maus/Trackpad (`pointer: fine`)
 * öffnet `tel:` am Rechner oft nichts oder eine fremde App (Clarity: tote
 * Klicks auf die Nummer), daher kopiert der Klick die Nummer zusätzlich und
 * zeigt gut 2 s eine Sprechblase „Nummer kopiert“ am Link (aria-live).
 * Kein `preventDefault`: der tel:-Link läuft unverändert weiter. Auf
 * Touch-Geräten bleibt es beim Anrufen.
 *
 * Der Link sitzt in einem `relative inline-flex`-Span (Anker der Sprechblase).
 * Soll er die volle Breite füllen (Buttons in `flex-col`), dem Link `w-full`
 * geben.
 */
export function PhoneCopyLink({
  href,
  number,
  className,
  wrapperClassName,
  children,
  ariaLabel,
  onClick,
  placement = "bottom",
  tone = "dark",
}: {
  href: string;
  /** Text für die Zwischenablage (wie angezeigt). */
  number: string;
  className?: string;
  wrapperClassName?: string;
  children: ReactNode;
  ariaLabel?: string;
  /** Läuft vor dem Kopieren, z. B. um ein Menü auf Touch zu schließen. */
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  /** Sprechblase unter (Standard) oder über dem Link, z. B. bei fixierten Leisten unten. */
  placement?: "bottom" | "top";
  /** `light` auf dunklen Flächen. */
  tone?: "dark" | "light";
}) {
  const locale = useLocale();
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (!isFinePointer()) return;
    try {
      // Direkt im Klick-Handler (Nutzergeste), Ablehnung still abfangen.
      navigator.clipboard
        .writeText(number)
        .then(() => {
          setCopied(true);
          window.clearTimeout(timer.current);
          timer.current = window.setTimeout(() => setCopied(false), 2500);
        })
        .catch(() => {});
    } catch {
      // Keine Zwischenablage (z. B. unsicherer Kontext): nur der tel:-Link.
    }
  };

  const light = tone === "light";
  const top = placement === "top";

  return (
    <span className={cn("relative inline-flex", wrapperClassName)}>
      <a href={href} onClick={handleClick} aria-label={ariaLabel} className={className}>
        {children}
      </a>
      <span
        role="status"
        aria-live="polite"
        className={cn(
          "pointer-events-none absolute left-1/2 z-50 -translate-x-1/2",
          top ? "bottom-full mb-2.5" : "top-full mt-2.5",
        )}
      >
        {copied ? (
          <span
            className={cn(
              "relative flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-[13px] font-semibold leading-tight shadow-lg motion-safe:animate-[fadeIn_160ms_ease-out_both]",
              light ? "bg-white text-ink-900 ring-1 ring-black/5" : "bg-ink-900 text-white",
            )}
          >
            <span
              aria-hidden
              className={cn(
                "absolute left-1/2 h-2.5 w-2.5 -translate-x-1/2 rotate-45",
                top ? "-bottom-1" : "-top-1",
                light ? "bg-white" : "bg-ink-900",
              )}
            />
            <Check className="relative h-4 w-4 shrink-0 text-success-500" strokeWidth={2.5} aria-hidden />
            <span className="relative">{COPIED[locale] ?? COPIED.de}</span>
          </span>
        ) : null}
      </span>
    </span>
  );
}
